import React, { useState, useEffect, useMemo } from 'react';
import ApiClient, { Channel } from '../api/client';
import { ModalShell } from './ui';

interface ChannelSelectorProps {
  formId: number;
  apiClient: ApiClient;
  attachedChannels: Channel[];
  onChannelsChange: (channels: Channel[]) => void;
  onClose: () => void;
  mode?: 'regular' | 'flagged' | 'spam';
}

type ChannelType = Channel['type'];
type ChannelFilter = 'all' | ChannelType;

const channelTypeOrder: ChannelType[] = [
  'slack',
  'email',
  'pushover',
  'convertkit',
  'mautic',
  'twilio-sms',
  'espocrm',
  'pbx-crm',
  'webhook',
];

const channelTypeLabels: Record<ChannelType, string> = {
  slack: 'Slack',
  email: 'Email',
  pushover: 'Pushover',
  convertkit: 'ConvertKit',
  mautic: 'Mautic',
  'twilio-sms': 'Twilio SMS',
  espocrm: 'EspoCRM',
  'pbx-crm': 'PBX/IVR',
  webhook: 'Webhook',
};

const ChannelSelector: React.FC<ChannelSelectorProps> = ({
  formId,
  apiClient,
  attachedChannels,
  onChannelsChange,
  onClose,
  mode = 'regular',
}) => {
  const isFlagged = mode === 'flagged';
  const isSpam = mode === 'spam';
  const [allChannels, setAllChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    new Set(attachedChannels.map((c) => c.id))
  );
  const [activeType, setActiveType] = useState<ChannelFilter>('all');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadChannels();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const loadChannels = async () => {
    setLoading(true);
    try {
      const channels = await apiClient.listChannels();
      setAllChannels(channels);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load channels');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (channelId: number) => {
    const updated = new Set(selectedIds);
    if (updated.has(channelId)) {
      updated.delete(channelId);
    } else {
      updated.add(channelId);
    }
    setSelectedIds(updated);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentIds = new Set(attachedChannels.map((c) => c.id));
      const toAdd = Array.from(selectedIds).filter((id) => !currentIds.has(id));
      const toRemove = Array.from(currentIds).filter((id) => !selectedIds.has(id));

      for (const id of toAdd) {
        if (isSpam) {
          await apiClient.attachSpamChannelToForm(formId, id);
        } else if (isFlagged) {
          await apiClient.attachFlaggedChannelToForm(formId, id);
        } else {
          await apiClient.attachChannelToForm(formId, id);
        }
      }
      for (const id of toRemove) {
        if (isSpam) {
          await apiClient.detachSpamChannelFromForm(formId, id);
        } else if (isFlagged) {
          await apiClient.detachFlaggedChannelFromForm(formId, id);
        } else {
          await apiClient.detachChannelFromForm(formId, id);
        }
      }

      const selectedChannels = allChannels.filter((c) => selectedIds.has(c.id));
      onChannelsChange(selectedChannels);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update channels');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSelectedIds(new Set(attachedChannels.map((c) => c.id)));
  };

  const typeCounts = useMemo(() => {
    return allChannels.reduce((acc, channel) => {
      acc[channel.type] = (acc[channel.type] ?? 0) + 1;
      return acc;
    }, {} as Record<ChannelType, number>);
  }, [allChannels]);

  const filteredChannels = useMemo(() => {
    return activeType === 'all'
      ? allChannels
      : allChannels.filter((channel) => channel.type === activeType);
  }, [activeType, allChannels]);

  const groupedChannels = useMemo(() => {
    return filteredChannels.reduce(
      (acc, channel) => {
        if (!acc[channel.type]) {
          acc[channel.type] = [];
        }
        acc[channel.type].push(channel);
        return acc;
      },
      {} as Record<ChannelType, Channel[]>
    );
  }, [filteredChannels]);

  return (
    <ModalShell
      title={isSpam ? 'Manage Spam Channels' : isFlagged ? 'Manage Flagged Channels' : 'Manage Channels'}
      onClose={onClose}
      className="channel-selector-modal"
      footer={
        <>
          <button className="btn btn-secondary" onClick={handleReset} disabled={saving}>
            Reset
          </button>
          <span className="modal-footer-spacer" />
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </>
      }
    >
          {error && (
            <div className="alert alert-error">
              <span>Warning</span>
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="empty-state">
              <div className="loading-spinner"></div>
              <p>Loading channels...</p>
            </div>
          ) : allChannels.length === 0 ? (
            <div className="empty-state">
              <p>No channels created yet. Create a channel first.</p>
            </div>
          ) : (
            <div className="channel-selector-body">
              <div className="channel-selector-toolbar">
                <select
                  value={activeType}
                  onChange={(event) => setActiveType(event.target.value as ChannelFilter)}
                  disabled={saving}
                >
                  <option value="all">All service types ({allChannels.length})</option>
                  {channelTypeOrder
                    .filter((type) => typeCounts[type])
                    .map((type) => (
                      <option key={type} value={type}>
                        {channelTypeLabels[type]} ({typeCounts[type]})
                      </option>
                    ))}
                </select>
                <span>{selectedIds.size} selected</span>
              </div>

              {filteredChannels.length === 0 ? (
                <div className="empty-state compact-empty">
                  <p>No channels match this service type.</p>
                </div>
              ) : (
                <div className="channel-selector-list">
                  {Object.entries(groupedChannels).map(([type, channels]) => (
                    <section key={type} className="channel-selector-group">
                      <h4>
                        {channelTypeLabels[type as ChannelType] || type}
                      </h4>
                      <div className="channel-selector-options">
                        {channels.map((channel) => {
                          const isSelected = selectedIds.has(channel.id);
                          return (
                            <label
                              key={channel.id}
                              className={`channel-selector-option ${isSelected ? 'selected' : ''} ${saving ? 'disabled' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggle(channel.id)}
                                disabled={saving}
                              />
                              <div>
                                <strong>
                                  {channel.name}
                                </strong>
                                <span>
                                  {channel.createdAt ? new Date(channel.createdAt).toLocaleDateString() : 'N/A'}
                                </span>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </div>
          )}
    </ModalShell>
  );
};

export default ChannelSelector;
